import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpClient,
} from '@angular/common/http';
import {
  Observable,
  catchError,
  concatMap,
  delay,
  firstValueFrom,
  iif,
  of,
  retryWhen,
  throwError,
  from,
  switchMap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { MaintenanceService } from '../../services';

export interface RetryRequestOptions {
  maximumRetries: number;
  retryDelay: number;
}

/**
 * Prefixes all requests with `environment.SERVER_URL`. and adds withCredentials and adds csrf token header
 */
@Injectable({
  providedIn: 'root',
})
export class CustomInterceptor implements HttpInterceptor {
  private retryRequestOptions: RetryRequestOptions = {
    maximumRetries: 2,
    retryDelay: 200, //milliseconds
  };
  private readonly maximumCsrfRetries = 2;
  private pendingCsrfRefresh?: Promise<void>;

  constructor(
    private maintenanceService: MaintenanceService,
    private injector: Injector
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!/^(http|https):/i.test(request.url)) {
      request = request.clone({
        url: environment.API_SERVER_URL + request.url,
      });
    }

    const cookieheaderName = 'X-CSRF-Token';
    let csrfToken = (window as any).csrfToken || null;
    if (csrfToken !== null) {
      request = request.clone({
        headers: request.headers.set(cookieheaderName, csrfToken),
      });
    }

    return this.send(request, next, 0);
  }

  private send(
    request: HttpRequest<any>,
    next: HttpHandler,
    csrfRetries: number
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Handle server errors - let MaintenanceService handle the logic
        if (
          error.status === 502 ||
          error.status === 503 ||
          error.status === 504
        ) {
          if (!this.maintenanceService.gameStartupActive && !this.maintenanceService.gameRecoveryActive) {
            this.maintenanceService.setServerError(error, 'HTTP');
          }
        }

        // A CSRF rejection proves the action did not run, so any request can safely repeat
        if (this.isCsrfError(error) && csrfRetries < this.maximumCsrfRetries) {
          return this.retryWithFreshCsrfToken(request, next, csrfRetries, error);
        }

        return throwError(() => error);
      })
    );
  }

  retryPipe<T>() {
    return retryWhen<T>((errors: Observable<HttpErrorResponse>) =>
      errors.pipe(
        // Use concat map to keep the errors in order and make sure they
        // aren't executed in parallel
        concatMap((error: HttpErrorResponse, retryId: number) =>
          // Executes a conditional Observable depending on the result
          // of the first argument
          iif(
            () =>
              retryId >= this.retryRequestOptions.maximumRetries ||
              error.status <= 500,
            // If the condition is true we throw the error (the last error)
            this.returnError(error),
            // Otherwise we pipe this back into our stream and delay the retry
            this.retryError(error, retryId, this.retryRequestOptions.retryDelay)
          )
        )
      )
    );
  }

  returnError(error: HttpErrorResponse) {
    return throwError(error);
  }

  retryError(error: HttpErrorResponse, retryId: number, retryDelay: number) {
    // retry request with increasing delay between each attemp
    return of(error).pipe(delay(retryDelay * retryId));
  }

  private isCsrfError(error: HttpErrorResponse): boolean {
    // Sails answers a rejected token with 403 and the plain text body 'CSRF mismatch'.
    // Only the body says so: over HTTP/2 the status text is empty.
    const body = error.error;
    const text = typeof body === 'string' ? body : body?.message || '';
    return error.status === 403 && /csrf/i.test(text);
  }

  private retryWithFreshCsrfToken(
    request: HttpRequest<any>,
    next: HttpHandler,
    csrfRetries: number,
    error: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    return from(this.refreshCsrfToken()).pipe(
      catchError(() => throwError(() => error)),
      switchMap(() =>
        this.send(
          request.clone({
            headers: request.headers.set('X-CSRF-Token', (window as any).csrfToken),
          }),
          next,
          csrfRetries + 1
        )
      )
    );
  }

  /** Parallel requests rejected together share one refresh, so they all end up with the same token. */
  private refreshCsrfToken(): Promise<void> {
    if (!this.pendingCsrfRefresh) {
      // Use direct HttpClient to avoid circular dependency
      const httpClient = this.injector.get(HttpClient);
      this.pendingCsrfRefresh = firstValueFrom(
        httpClient.get<any>(`${environment.API_SERVER_URL}/csrf-token`)
      )
        .then((response) => {
          (window as any).csrfToken = response._csrf;
        })
        .finally(() => {
          this.pendingCsrfRefresh = undefined;
        });
    }
    return this.pendingCsrfRefresh;
  }
}
